/**
 * Filter bar for the contact directory.
 *
 * Every control writes to the URL rather than to local state, so a filtered
 * view is linkable — "the primary contacts" is a thing one person sends
 * another before going on leave.
 *
 * `primaryOnly` is a toggle rather than a dropdown for the same reason
 * `overdue` is on the other lists: it answers a yes/no question the directory
 * exists for, and a three-state select would bury it behind two clicks.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.5, PRD §5.7, §5.9
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ORG_TYPES } from '../../../components/domain/org-types';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { Icon } from '../../../components/ui/Icon';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { filterHandler } from '../../../lib/filter-handler';

export interface ContactFiltersProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
}

/** The controls, shared by the inline bar and the sheet. */
function FilterControls({
  filters,
  onFilterChange,
}: Omit<ContactFiltersProps, 'onClear'>): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <label className="flex-1">
        <span className="sr-only">{t('contact.filters.search')}</span>
        <Input
          type="search"
          value={filters['q'] ?? ''}
          onChange={(event) => {
            onFilterChange('q', event.target.value);
          }}
          placeholder={t('contact.filters.searchPlaceholder')}
        />
      </label>

      <Select
        value={filters['orgType']}
        onValueChange={filterHandler('orgType', onFilterChange)}
        options={ORG_TYPES.map((orgType) => ({
          value: orgType,
          label: t(`contact.orgType.${orgType}`),
        }))}
        placeholder={t('contact.filters.anyOrgType')}
        ariaLabel={t('contact.filters.orgType')}
        className="sm:w-56"
      />

      {/* The handover view: who to call about each startup when the owner is
          away (PRD §5.9). */}
      <Button
        variant={filters['primaryOnly'] === 'true' ? 'primary' : 'secondary'}
        size="sm"
        aria-pressed={filters['primaryOnly'] === 'true'}
        onClick={() => {
          /* Cleared by removing the key, not by storing `"false"` — the URL is
             the state, and `?primaryOnly=false` reads as an applied filter. */
          onFilterChange('primaryOnly', filters['primaryOnly'] === 'true' ? '' : 'true');
        }}
      >
        {t('contact.filters.primaryOnly')}
      </Button>
    </>
  );
}

export function ContactFilters({
  filters,
  onFilterChange,
  onClear,
}: ContactFiltersProps): React.JSX.Element {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const appliedCount = Object.keys(filters).length;

  return (
    <>
      {/* Inline bar, 768px and up. */}
      <div className="gap-s2 hidden items-center md:flex">
        <FilterControls filters={filters} onFilterChange={onFilterChange} />
        {appliedCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('contact.filters.clear')}
          </Button>
        ) : null}
      </div>

      {/* Below 768px: one button opening a sheet, with the applied count on it
          so active filters are visible without opening it. */}
      <div className="md:hidden">
        <Button
          variant="secondary"
          onClick={() => {
            setSheetOpen(true);
          }}
          className="w-full"
        >
          <Icon name="search" />
          {appliedCount === 0
            ? t('actions.filter')
            : t('contact.filters.applied', { count: appliedCount })}
        </Button>

        <Dialog
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={t('actions.filter')}
          footer={
            <>
              <Button variant="ghost" onClick={onClear}>
                {t('contact.filters.clear')}
              </Button>
              <Button
                onClick={() => {
                  setSheetOpen(false);
                }}
              >
                {t('actions.filter')}
              </Button>
            </>
          }
        >
          <div className="gap-s3 flex flex-col">
            <FilterControls filters={filters} onFilterChange={onFilterChange} />
          </div>
        </Dialog>
      </div>
    </>
  );
}
